import { useCallback, useEffect, useState } from "react";

import useSegmentTrackEvent from "../../utils/useSegmentTrackEvent";
import { type TimeRange } from "./Dashboard/ClusterDetail/Components/TimeRangeSelector";

const baseK8sEventProperties = {
  pageType: "Optimize",
  pageSubType: "FSK8S",
  feature: "FSK8S",
};

interface ClusterDetailViewedEvent {
  clusterId: string;
  clusterType: string;
}

interface ClusterAgentInstallStartedEvent {
  clusterId: string;
  clusterType: string;
  agentStatus: string;
}

interface K8sDashboardViewedEvent {
  timeRange: TimeRange;
  totalSpend: number;
  podWaste: number;
  totalClusters: number;
}

type EventTypes = {
  "Cluster Detail Viewed": ClusterDetailViewedEvent;
  "K8s Dashboard Viewed": K8sDashboardViewedEvent;
  "Cluster Agent Install Started": ClusterAgentInstallStartedEvent;
};

export function useTrackK8sEvent() {
  const { trackEvent } = useSegmentTrackEvent({ allowNullUser: false });

  return useCallback(
    <T extends keyof EventTypes>(type: T, options: EventTypes[T]) => {
      trackEvent(type, {
        ...baseK8sEventProperties,
        ...options,
      });
    },
    [trackEvent]
  );
}

interface UseTrackK8sEventOnceProps<T extends keyof EventTypes> {
  name: T;
  properties: EventTypes[T];
  condition?: boolean;
}

/* Wrapper over trackEvent, used for "page viewed" events. Avoids sending the event more than once */
export const useTrackK8sEventOnce = <T extends keyof EventTypes>({
  name,
  properties,
  condition,
}: UseTrackK8sEventOnceProps<T>) => {
  const trackEvent = useTrackK8sEvent();

  const [eventWasSent, setEventWasSent] = useState(false);

  const sendEvent = useCallback(() => {
    trackEvent(name, properties);
    setEventWasSent(true);
  }, [name, properties, trackEvent]);

  useEffect(() => {
    if (condition && !eventWasSent) {
      sendEvent();
    }
  }, [sendEvent, condition, eventWasSent]);
};
